<template>
    <div>
        <VJoyPanel style="width: 100%">
            <template #panel-title>{{ t('projectclient.quote.view.quote-details.title') }}</template>
            <template #panel-body>
                <TDataTable :value="formattedQuoteLines" class="quotelines-table" :scrollable="false">
                    <TColumn v-for="column of columns" :key="column" class="quotelines-table_td">
                        <template #header>
                            <div class="quotelines-table_header / flex" :class="`column-${column}`">
                                <VJoyText preset="primary-200">{{ columnTitle[column] }}</VJoyText>
                            </div>
                        </template>
                        <template #body="{data}">
                            <QuoteLineCell v-if="column === 'description'" :column="column" :rich-content="data.description" />
                            <QuoteLineCell v-else :column="column" :content="data[column]" />
                        </template>
                    </TColumn>
                </TDataTable>
            </template>
        </VJoyPanel>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import type {TupleToUnion} from 'type-fest';
    import {useFormat, useTranslation} from '#imports';
    import {createDataTableComponents} from '@malt/joy-entrypoint/src/vue/utils';
    import {VJoyPanel, VJoyText} from '@maltjoy/core-vue';
    import type {FixedPriceQuoteLineResource, Currency} from '~/openapi';
    import QuoteLineCell from './QuoteLineCell.vue';

    const {t} = useTranslation();

    const props = defineProps<{
        quoteLines: FixedPriceQuoteLineResource[];
        currency: Currency;
    }>();

    const {formatAmount} = useFormat();

    const columnTitle: Record<TupleToUnion<typeof columns>, string> = {
        description: t('projectclient.quote.view.quote-details.fixed-price.label.tasks'),
        quantity: t('projectclient.quote.view.quote-details.fixed-price.label.quantity'),
        unitPriceExcludingTaxes: t('projectclient.quote.view.quote-details.fixed-price.label.unit-price'),
        totalPriceExcludingTaxes: t('projectclient.quote.view.quote-details.fixed-price.label.total'),
    };

    const columns = [
        'description',
        'quantity',
        'unitPriceExcludingTaxes',
        'totalPriceExcludingTaxes',
    ] as const satisfies (keyof FixedPriceQuoteLineResource)[];

    const {TColumn, TDataTable} = createDataTableComponents<FixedPriceQuoteLineResource>();

    const formattedQuoteLines = computed(() => {
        return props.quoteLines.map((line) => ({
            ...line,
            unitPriceExcludingTaxes: formatAmount(props.currency, line.unitPriceExcludingTaxes, {decimals: 2}),
            totalPriceExcludingTaxes: formatAmount(props.currency, line.totalPriceExcludingTaxes ?? 0, {
                decimals: 2,
            }),
        }));
    });
</script>

<style lang="scss" scoped>
    .quotelines-table {
        :deep(tr:last-child > td) {
            border-bottom: none;
        }

        :deep(td),
        :deep(th) {
            vertical-align: top;
            padding-right: var(--joy-core-spacing-2);
            padding-left: 0;
        }

        &_header {
            display: flex;
            justify-content: flex-end;
            width: 100%;
            flex: 0 0 auto;
            white-space: nowrap;

            &.column-description {
                justify-content: flex-start;
                padding-right: var(--joy-core-spacing-2);
            }

            &:not(.column-description) {
                padding-left: var(--joy-core-spacing-2);
            }

            &.column-quantity,
            &.column-unitPriceExcludingTaxes {
                width: 80px;
            }

            &.column-totalPriceExcludingTaxes {
                width: 110px;
            }
        }
    }
</style>
